import classNames from 'classnames';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';

import Caret from '../../../../components/icons/Caret';
import { usePrefersReducedMotion } from '../../../../utils/usePrefersReducedMotion';

import mobileNavStyle from '../mobile-nav.module.css';
import style from './mobile-nav-list-item.module.css';

interface MobileNavListItemProps {
  itemId: string;
  itemLabel: string;
  onSetOpenMenuId: React.MouseEventHandler<HTMLButtonElement>;
  openMenuId: null | string;
}

const MobileNavListItem = ({
  children,
  itemId,
  itemLabel,
  onSetOpenMenuId,
  openMenuId,
}: PropsWithChildren<MobileNavListItemProps>) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const prefersReducedMotion = usePrefersReducedMotion();

  const animationOptions: KeyframeAnimationOptions = useMemo(() => {
    return {
      duration: prefersReducedMotion ? 0 : 200,
      easing: 'ease-in-out',
      fill: 'forwards',
    };
  }, [prefersReducedMotion]);

  const isOpen = openMenuId === itemId;

  const handleOpen = useCallback(() => {
    const nav = document.querySelector(`.${mobileNavStyle.menu} nav`);

    if (dialogRef.current) {
      dialogRef.current.showModal();

      const dialogSlideIn: Keyframe[] = [
        { opacity: 0, transform: 'translate3d(30%, 0, 0)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' },
      ];

      dialogRef.current.animate(dialogSlideIn, animationOptions);
    }

    if (nav) {
      const navSlideOut: Keyframe[] = [
        { opacity: 1, transform: 'translate3d(0, 0, 0)' },
        { opacity: 0, transform: 'translate3d(-50px, 0, 0)' },
      ];

      nav.animate(navSlideOut, animationOptions);
    }
  }, [animationOptions]);

  const handleClose = useCallback(() => {
    const nav = document.querySelector(`.${mobileNavStyle.menu} nav`);

    if (dialogRef.current) {
      const slideOut: Keyframe[] = [
        { opacity: 1, transform: 'translate3d(0, 0, 0)' },
        { opacity: 0, transform: 'translate3d(30%, 0, 0)' },
      ];

      const animation = dialogRef.current.animate(slideOut, animationOptions);

      animation.addEventListener('finish', () => {
        dialogRef.current?.close();
      });
    }

    if (nav) {
      const navSlideIn: Keyframe[] = [
        { opacity: 0, transform: 'translate3d(-50px, 0, 0)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' },
      ];

      nav.animate(navSlideIn, animationOptions);
    }
  }, [animationOptions]);

  const isDialogOpen = dialogRef.current?.open;

  useEffect(() => {
    if (isOpen && !isDialogOpen) {
      handleOpen();
    }
    else if (!isOpen && isDialogOpen) {
      handleClose();
    }
  }, [
    handleClose,
    handleOpen,
    isOpen,
    isDialogOpen,
  ]);

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLDialogElement>) => {
    if (evt.key === 'Escape') {
      evt.preventDefault();
      handleClose();
    }
  };

  return (
    <li
      className={style.listItem}
      key={itemId}
    >
      <button
        className={style.listItemButton}
        data-menu-id={itemId}
        onClick={onSetOpenMenuId}
      >
        {itemLabel}

        <Caret direction="right" />
      </button>

      <dialog
        className={style.listItemDialog}
        onKeyDown={handleKeyDown}
        ref={dialogRef}
      >
        <div className={style.listItemDialogContainer}>
          <button
            className={classNames(style.listItemButton, style.backButton)}
            onClick={onSetOpenMenuId}
          >
            <Caret direction="left" />

            Back
          </button>

          {children}
        </div>
      </dialog>
    </li>
  );
};

export default MobileNavListItem;
